// /src/pages/PrivacyPolicy.js
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: 'easeOut' }
  }
};

const PrivacyPolicy = () => {
  return (
    <>
    <Helmet>
      <title>Privacy Policy | MDeRCM Healthcare Revenue Cycle Management</title>
      <meta name="description" content="MDeRCM's privacy policy outlines how we collect, use, and protect your personal information. We are fully HIPAA compliant and committed to data security." />
      <meta name="keywords" content="MDeRCM privacy policy, healthcare data privacy, HIPAA compliant billing, medical billing data protection" />
      <link rel="canonical" href="https://mdercm.com/privacy/" />
      <meta property="og:title" content="Privacy Policy | MDeRCM Healthcare Revenue Cycle Management" />
      <meta property="og:description" content="MDeRCM's commitment to protecting your privacy and personal information with full HIPAA compliance." />
      <meta property="og:url" content="https://mdercm.com/privacy/" />
      <meta property="og:type" content="website" />
    </Helmet>
    <div style={{ fontFamily: 'Poppins, sans-serif', backgroundColor: '#f9fafb' }}>
      {/* Hero Section */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          background: 'linear-gradient(135deg, #001F2B 0%, #003D5C 100%)',
          color: '#ffffff',
          padding: '60px 20px',
          textAlign: 'center'
        }}
      >
        <h1 style={{ 
          fontSize: 'clamp(28px, 5vw, 42px)', 
          fontWeight: '700', 
          marginBottom: '16px',
          lineHeight: 1.2
        }}>
          Privacy Policy - MDeRCM
        </h1>
        <p style={{ 
          fontSize: 'clamp(16px, 2vw, 18px)', 
          color: '#dddddd',
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          Your privacy matters. Learn how we protect your data in our healthcare revenue cycle management services.
        </p>
        <p style={{ 
          color: '#cccccc', 
          fontSize: '14px', 
          marginTop: '20px' 
        }}>
          Last Updated: June 4, 2025
        </p>
      </motion.div>

      {/* Main Content */}
      <div style={{ 
        padding: '60px 20px', 
        maxWidth: '1000px', 
        margin: '0 auto' 
      }}>
        {/* Introduction */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            Introduction to Our Privacy Commitment
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '16px' 
          }}>
            MDeRCM ("we," "our," or "us") is committed to safeguarding your personal data and maintaining the highest standards of privacy and security. As a leading provider of <strong>healthcare revenue cycle management services</strong> and <strong>medical billing outsourcing services</strong>, we understand the critical importance of protecting sensitive healthcare information.
          </p>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '16px' 
          }}>
            This Privacy Policy explains how we collect, use, share, and protect your information when you interact with our services—including visiting our website, using our AI-driven <Link to="/healthcare/ai" style={{ color: '#F26522', fontWeight: '600' }}>healthcare AI tools</Link>, engaging with our <strong>revenue cycle management consulting services</strong>, or communicating with our team.
          </p>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333' 
          }}>
            Our <strong>outsourced revenue cycle management services</strong> are designed with privacy-by-design principles, ensuring HIPAA compliance and data security at every step. If you have questions or concerns about this policy, you can reach us at{' '}
            <a href="mailto:sales@mdercm.com" style={{ color: '#F26522', fontWeight: '600' }}>sales@mdercm.com</a>.
          </p>
        </motion.section>

        {/* Section 1 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            1. What Information We Collect
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '16px' 
          }}>
            As one of the leading <strong>revenue cycle management services companies</strong>, we collect personal data when you:
          </p>
          <ul style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333',
            marginLeft: '30px',
            marginBottom: '20px'
          }}>
            <li style={{ marginBottom: '10px' }}>Fill out forms or contact us via our website for <strong>medical revenue cycle management services</strong></li>
            <li style={{ marginBottom: '10px' }}>Subscribe to updates, newsletters, or request a demo of our <Link to="/healthcare/ai" style={{ color: '#F26522' }}>AI-powered RCM solutions</Link></li>
            <li style={{ marginBottom: '10px' }}>Use our <Link to="/healthcare/ai/eligibility-check" style={{ color: '#F26522' }}>insurance eligibility verification</Link>, <Link to="/healthcare/ai/prior-authorization" style={{ color: '#F26522' }}>prior authorization</Link>, or other AI tools</li>
            <li style={{ marginBottom: '10px' }}>Engage with our <strong>hospital revenue cycle management</strong> services</li>
            <li style={{ marginBottom: '10px' }}>Schedule consultations for <strong>healthcare cost reduction consulting</strong></li>
          </ul>
          
          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#001F2B', 
            marginTop: '24px',
            marginBottom: '12px' 
          }}>
            Types of Information We Collect:
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '20px'
          }}>
            {[
              '👤 Name and Contact Information',
              '📧 Email Address',
              '📞 Phone Number',
              '🏥 Organization/Practice Details',
              '💼 Job Role and Title',
              '📊 Usage Data and Analytics',
              '🔒 Account Credentials',
              '💳 Billing Information (encrypted)'
            ].map((item, i) => (
              <div key={i} style={{
                backgroundColor: '#f9fafb',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                fontSize: '15px',
                color: '#333'
              }}>
                {item}
              </div>
            ))}
          </div>
          
          <div style={{
            backgroundColor: '#FFF3E0',
            border: '2px solid #F26522',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <p style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#001F2B', 
              marginBottom: '8px' 
            }}>
              ⚠️ Important: Protected Health Information (PHI)
            </p>
            <p style={{ 
              fontSize: '15px', 
              lineHeight: '1.7', 
              color: '#333' 
            }}>
              <strong>We do not collect sensitive personal health information (PHI)</strong> through our website unless explicitly provided through secure, encrypted channels and with your informed consent. Our <strong>medical billing and coding outsourcing</strong> services maintain strict HIPAA compliance and use secure, encrypted systems for all healthcare data processing.
            </p>
          </div>
        </motion.section>

        {/* Section 2 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            2. How We Use Your Information
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '20px' 
          }}>
            As a trusted provider among the <strong>best revenue cycle management companies</strong>, we use your data responsibly to:
          </p>
          
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {[
              {
                icon: '✅',
                title: 'Service Delivery & Improvement',
                desc: 'Provide and continuously improve our healthcare revenue cycle management services, including claims processing, denial management, and payment posting.'
              },
              {
                icon: '💬',
                title: 'Customer Support & Communication',
                desc: 'Respond to inquiries about our medical billing outsourcing services, provide technical support, and share important service updates.'
              },
              {
                icon: '📊',
                title: 'Analytics & Optimization',
                desc: 'Analyze usage patterns to enhance our AI-powered tools and implement healthcare cost reduction strategies for better performance.'
              },
              {
                icon: '📧',
                title: 'Marketing Communications',
                desc: 'Send newsletters, product updates, and educational content about revenue cycle management best practices (you can opt-out anytime).'
              },
              {
                icon: '🎯',
                title: 'Personalization',
                desc: 'Customize your experience with our revenue cycle management consulting services based on your specialty and needs.'
              },
              {
                icon: '🛡️',
                title: 'Security & Compliance',
                desc: 'Ensure legal compliance, prevent fraud, maintain HIPAA standards, and protect the integrity of our medical revenue cycle management services.'
              }
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                gap: '16px',
                padding: '20px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ fontSize: '28px', minWidth: '40px' }}>{item.icon}</div>
                <div>
                  <h4 style={{ 
                    fontSize: '18px', 
                    fontWeight: '600', 
                    color: '#001F2B', 
                    marginBottom: '8px' 
                  }}>
                    {item.title}
                  </h4>
                  <p style={{ fontSize: '15px', lineHeight: '1.7', color: '#555' }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Section 3 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            3. Legal Grounds for Processing Your Data
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '20px' 
          }}>
            Our <strong>outsourced revenue cycle management services</strong> process your data only when we have a valid legal basis:
          </p>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '20px'
          }}>
            {[
              {
                title: 'Consent',
                examples: 'Newsletter signup, marketing communications, free trial requests',
                icon: '✋'
              },
              {
                title: 'Contractual Necessity',
                examples: 'Demo requests, service agreements, medical billing outsourcing contracts',
                icon: '📝'
              },
              {
                title: 'Legitimate Interest',
                examples: 'Service improvement, analytics, fraud prevention, healthcare cost reduction strategies',
                icon: '⚖️'
              },
              {
                title: 'Legal Obligations',
                examples: 'HIPAA compliance, tax requirements, data retention laws, audit requirements',
                icon: '⚖️'
              }
            ].map((item, i) => (
              <div key={i} style={{
                padding: '24px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '2px solid #e5e7eb',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>{item.icon}</div>
                <h4 style={{ 
                  fontSize: '18px', 
                  fontWeight: '700', 
                  color: '#001F2B', 
                  marginBottom: '12px' 
                }}>
                  {item.title}
                </h4>
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '1.6', 
                  color: '#555' 
                }}>
                  {item.examples}
                </p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Section 4 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            4. Data Sharing and Third Parties
          </h2>
          
          <div style={{
            backgroundColor: '#E8F5E9',
            border: '2px solid #4CAF50',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '24px'
          }}>
            <p style={{ 
              fontSize: '17px', 
              fontWeight: '700', 
              color: '#001F2B', 
              marginBottom: '8px' 
            }}>
              ✓ We Do NOT Sell Your Personal Information
            </p>
            <p style={{ 
              fontSize: '15px', 
              lineHeight: '1.7', 
              color: '#333' 
            }}>
              As one of the leading <strong>revenue cycle management services companies USA</strong>, we are committed to protecting your privacy. We will never sell, rent, or trade your personal information to third parties for marketing purposes.
            </p>
          </div>

          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#001F2B', 
            marginBottom: '16px' 
          }}>
            We May Share Limited Data With:
          </h3>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {[
              {
                category: 'Trusted Service Providers',
                desc: 'Cloud hosting, email delivery, payment processing, and analytics partners who help us deliver our medical billing outsourcing services. All partners sign strict confidentiality agreements.'
              },
              {
                category: 'Healthcare Partners',
                desc: 'When providing hospital revenue cycle management or integrated services, we may share data with authorized healthcare providers under HIPAA-compliant Business Associate Agreements (BAAs).'
              },
              {
                category: 'Legal Authorities',
                desc: 'If required by law, court order, or government request, or to protect rights, property, or safety in connection with our healthcare revenue cycle management services.'
              },
              {
                category: 'Business Transfers',
                desc: 'In the event of a merger, acquisition, or sale of assets, your data may be transferred to the new entity, subject to the same privacy protections.'
              }
            ].map((item, i) => (
              <div key={i} style={{
                padding: '20px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <h4 style={{ 
                  fontSize: '17px', 
                  fontWeight: '700', 
                  color: '#F26522', 
                  marginBottom: '10px' 
                }}>
                  {item.category}
                </h4>
                <p style={{ 
                  fontSize: '15px', 
                  lineHeight: '1.7', 
                  color: '#555' 
                }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </motion.section>

        {/* Section 5 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            5. Data Security Measures
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '24px' 
          }}>
            As a provider of <strong>revenue cycle management consulting services</strong> for healthcare organizations, we implement industry-leading security measures to protect your data:
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '20px',
            marginBottom: '24px'
          }}>
            {[
              {
                icon: '🔐',
                title: 'End-to-End Encryption',
                desc: 'All data transmitted through our medical billing outsourcing services is encrypted using industry-standard SSL/TLS protocols.'
              },
              {
                icon: '🛡️',
                title: 'HIPAA Compliance',
                desc: 'Our healthcare revenue cycle management services meet all HIPAA Security Rule requirements including administrative, physical, and technical safeguards.'
              },
              {
                icon: '👁️',
                title: 'Access Controls',
                desc: 'Role-based access controls ensure that only authorized personnel can access sensitive data on a need-to-know basis.'
              },
              {
                icon: '📹',
                title: 'Audit Trails',
                desc: 'Comprehensive logging and monitoring of all data access and system activities for accountability and compliance.'
              },
              {
                icon: '🔄',
                title: 'Regular Backups',
                desc: 'Automated, encrypted backups ensure data availability and business continuity for our revenue cycle management services USA.'
              },
              {
                icon: '🧪',
                title: 'Security Testing',
                desc: 'Regular penetration testing, vulnerability assessments, and security audits to maintain the highest protection standards.'
              }
            ].map((item, i) => (
              <div key={i} style={{
                padding: '24px',
                backgroundColor: '#f9fafb',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>{item.icon}</div>
                <h4 style={{ 
                  fontSize: '17px', 
                  fontWeight: '700', 
                  color: '#001F2B', 
                  marginBottom: '10px' 
                }}>
                  {item.title}
                </h4>
                <p style={{ 
                  fontSize: '14px', 
                  lineHeight: '1.6', 
                  color: '#555' 
                }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>

          <div style={{
            backgroundColor: '#FFF9E6',
            border: '2px solid #FFC107',
            borderRadius: '8px',
            padding: '20px'
          }}>
            <p style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#001F2B', 
              marginBottom: '8px' 
            }}>
              ⚠️ Important Security Notice
            </p>
            <p style={{ 
              fontSize: '15px', 
              lineHeight: '1.7', 
              color: '#333' 
            }}>
              While we use advanced security measures to protect your data, no system can be 100% secure. Please avoid sharing highly sensitive information via unsecured channels. For secure transmission of PHI, use only our HIPAA-compliant portals within our <strong>outsourced revenue cycle management services</strong> platform.
            </p>
          </div>
        </motion.section>

        {/* Section 6 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            6. Data Retention Policy
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '20px' 
          }}>
            As part of our <strong>medical revenue cycle management services</strong>, we retain data only as long as necessary for:
          </p>

          <ul style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333',
            marginLeft: '30px',
            marginBottom: '24px'
          }}>
            <li style={{ marginBottom: '12px' }}>Providing ongoing <strong>healthcare revenue cycle management services</strong></li>
            <li style={{ marginBottom: '12px' }}>Meeting legal and regulatory compliance requirements (typically 7 years for healthcare records)</li>
            <li style={{ marginBottom: '12px' }}>Resolving disputes or enforcing agreements related to our <strong>medical billing outsourcing services</strong></li>
            <li style={{ marginBottom: '12px' }}>Legitimate business purposes such as analytics and service improvement</li>
          </ul>

          <div style={{
            backgroundColor: '#E3F2FD',
            border: '2px solid #2196F3',
            borderRadius: '8px',
            padding: '24px'
          }}>
            <h4 style={{ 
              fontSize: '18px', 
              fontWeight: '700', 
              color: '#001F2B', 
              marginBottom: '12px' 
            }}>
              📧 Request Data Deletion
            </h4>
            <p style={{ 
              fontSize: '15px', 
              lineHeight: '1.7', 
              color: '#333' 
            }}>
              You can request deletion of your personal data at any time by emailing{' '}
              <a href="mailto:sales@mdercm.com" style={{ color: '#F26522', fontWeight: '600' }}>sales@mdercm.com</a>. 
              Please note that certain data may need to be retained for legal compliance, contractual obligations, or to complete transactions related to our <strong>revenue cycle management services companies</strong> agreements.
            </p>
          </div>
        </motion.section>

        {/* Section 7 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            7. Your Privacy Rights
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '24px' 
          }}>
            When using our <strong>hospital revenue cycle management</strong> or other services, you have the following rights regarding your personal data:
          </p>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {[
              {
                right: 'Right to Access',
                desc: 'Request a copy of the personal data we hold about you in connection with our healthcare revenue cycle management services.',
                icon: '👁️'
              },
              {
                right: 'Right to Rectification',
                desc: 'Request correction of inaccurate or incomplete personal data in our medical billing outsourcing systems.',
                icon: '✏️'
              },
              {
                right: 'Right to Erasure',
                desc: 'Request deletion of your personal data (subject to legal retention requirements for revenue cycle management services).',
                icon: '🗑️'
              },
              {
                right: 'Right to Restrict Processing',
                desc: 'Request limitation on how we process your data within our outsourced revenue cycle management services.',
                icon: '⏸️'
              },
              {
                right: 'Right to Data Portability',
                desc: 'Receive your data in a structured, commonly used format for transfer to another service provider.',
                icon: '📦'
              },
              {
                right: 'Right to Object',
                desc: 'Object to processing of your data for marketing or legitimate interest purposes.',
                icon: '🚫'
              },
              {
                right: 'Right to Withdraw Consent',
                desc: 'Withdraw previously given consent for marketing communications or optional data processing.',
                icon: '✋'
              },
              {
                right: 'Right to Lodge a Complaint',
                desc: 'File a complaint with your local data protection authority regarding our privacy practices.',
                icon: '📢'
              }
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                gap: '20px',
                padding: '20px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                alignItems: 'flex-start'
              }}>
                <div style={{ fontSize: '32px', minWidth: '40px' }}>{item.icon}</div>
                <div>
                  <h4 style={{ 
                    fontSize: '18px', 
                    fontWeight: '700', 
                    color: '#F26522', 
                    marginBottom: '8px' 
                  }}>
                    {item.right}
                  </h4>
                  <p style={{ 
                    fontSize: '15px', 
                    lineHeight: '1.7', 
                    color: '#555' 
                  }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '24px',
            padding: '24px',
            backgroundColor: '#F3E5F5',
            borderRadius: '8px',
            border: '2px solid #9C27B0'
          }}>
            <h4 style={{ 
              fontSize: '18px', 
              fontWeight: '700', 
              color: '#001F2B', 
              marginBottom: '12px' 
            }}>
              📧 Exercise Your Rights
            </h4>
            <p style={{ 
              fontSize: '15px', 
              lineHeight: '1.7', 
              color: '#333' 
            }}>
              To exercise any of these rights regarding our <strong>revenue cycle management consulting services</strong>, please contact us at{' '}
              <a href="mailto:sales@mdercm.com" style={{ color: '#F26522', fontWeight: '600' }}>sales@mdercm.com</a>. 
              We will respond to your request within 30 days and may require verification of your identity to protect your privacy.
            </p>
          </div>
        </motion.section>

        {/* Section 8 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            8. Children's Privacy Protection
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '16px' 
          }}>
            Our <strong>healthcare revenue cycle management services</strong> and website are designed for healthcare professionals and organizations. Our services are not intended for individuals under 18 years of age.
          </p>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '16px' 
          }}>
            We do not knowingly collect personal information from minors through our <strong>medical billing outsourcing services</strong> platforms. If you believe we have inadvertently collected data from a minor, please contact us immediately at{' '}
            <a href="mailto:sales@mdercm.com" style={{ color: '#F26522', fontWeight: '600' }}>sales@mdercm.com</a>, 
            and we will promptly delete such information.
          </p>
          
          <div style={{
            backgroundColor: '#FFF3E0',
            border: '2px solid #FF9800',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <p style={{ 
              fontSize: '15px', 
              fontWeight: '600', 
              color: '#001F2B', 
              marginBottom: '8px' 
            }}>
              🔞 Age Verification
            </p>
            <p style={{ 
              fontSize: '15px', 
              lineHeight: '1.7', 
              color: '#333' 
            }}>
              By using our <strong>revenue cycle management services USA</strong> platform, you confirm that you are at least 18 years old or accessing under the supervision of a parent or legal guardian.
            </p>
          </div>
        </motion.section>

        {/* Section 9 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            9. Cookies and Tracking Technologies
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '20px' 
          }}>
            Our website uses cookies and similar tracking technologies to enhance your experience and analyze usage of our <strong>outsourced revenue cycle management services</strong>.
          </p>

          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#001F2B', 
            marginTop: '24px',
            marginBottom: '16px' 
          }}>
            Types of Cookies We Use:
          </h3>

          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginBottom: '24px'
          }}>
            {[
              {
                type: 'Essential Cookies',
                purpose: 'Required for basic website functionality and access to secure areas of our medical billing outsourcing services platform.',
                canDisable: false
              },
              {
                type: 'Analytics Cookies',
                purpose: 'Help us understand how visitors use our site to improve our healthcare revenue cycle management services.',
                canDisable: true
              },
              {
                type: 'Functional Cookies',
                purpose: 'Remember your preferences and settings for a personalized experience with our revenue cycle management consulting services.',
                canDisable: true
              },
              {
                type: 'Marketing Cookies',
                purpose: 'Track visitors across websites to display relevant advertisements about our medical revenue cycle management services.',
                canDisable: true
              }
            ].map((cookie, i) => (
              <div key={i} style={{
                padding: '20px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}>
                  <h4 style={{ 
                    fontSize: '17px', 
                    fontWeight: '700', 
                    color: '#F26522',
                    margin: 0
                  }}>
                    {cookie.type}
                  </h4>
                  <span style={{
                    fontSize: '13px',
                    fontWeight: '600',
                    padding: '4px 12px',
                    borderRadius: '20px',
                    backgroundColor: cookie.canDisable ? '#E8F5E9' : '#FFEBEE',
                    color: cookie.canDisable ? '#2E7D32' : '#C62828'
                  }}>
                    {cookie.canDisable ? '✓ Can Disable' : '✗ Required'}
                  </span>
                </div>
                <p style={{ 
                  fontSize: '15px', 
                  lineHeight: '1.7', 
                  color: '#555',
                  margin: 0
                }}>
                  {cookie.purpose}
                </p>
              </div>
            ))}
          </div>

          <div style={{
            backgroundColor: '#E3F2FD',
            border: '2px solid #2196F3',
            borderRadius: '8px',
            padding: '24px'
          }}>
            <h4 style={{ 
              fontSize: '18px', 
              fontWeight: '700', 
              color: '#001F2B', 
              marginBottom: '12px' 
            }}>
              🍪 Manage Cookie Preferences
            </h4>
            <p style={{ 
              fontSize: '15px', 
              lineHeight: '1.7', 
              color: '#333',
              marginBottom: '12px'
            }}>
              You can control and manage cookies through your browser settings. Please note that disabling cookies may affect the functionality of certain features in our <strong>hospital revenue cycle management</strong> platform.
            </p>
            <p style={{ 
              fontSize: '15px', 
              lineHeight: '1.7', 
              color: '#333' 
            }}>
              Most browsers allow you to: view cookies, delete cookies, block third-party cookies, block all cookies, or delete cookies when closing the browser. Learn more about cookie management for your specific browser.
            </p>
          </div>
        </motion.section>

        {/* Section 10 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            10. International Data Transfers
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '16px' 
          }}>
            As a provider of <strong>revenue cycle management services USA</strong>, we primarily operate within the United States. However, if you access our <strong>medical billing outsourcing services</strong> from outside the USA, please be aware that your data may be transferred to and processed in the United States or other countries where our service providers operate.
          </p>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '16px' 
          }}>
            We ensure appropriate safeguards are in place for international data transfers, including:
          </p>
          <ul style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333',
            marginLeft: '30px',
            marginBottom: '20px'
          }}>
            <li style={{ marginBottom: '10px' }}>Standard Contractual Clauses (SCCs) approved by relevant authorities</li>
            <li style={{ marginBottom: '10px' }}>Data Processing Agreements with all third-party service providers</li>
            <li style={{ marginBottom: '10px' }}>HIPAA compliance for all healthcare-related data transfers</li>
            <li style={{ marginBottom: '10px' }}>Encryption and security measures during data transmission</li>
          </ul>
        </motion.section>

        {/* Section 11 */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h2 style={{ 
            fontSize: '24px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            borderBottom: '3px solid #F26522',
            paddingBottom: '10px',
            display: 'inline-block'
          }}>
            11. Privacy Policy Updates
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333', 
            marginBottom: '16px' 
          }}>
            As we continue to enhance our <strong>healthcare revenue cycle management services</strong> and adapt to changing regulations, we may update this Privacy Policy periodically. We will notify you of significant changes through:
          </p>
          <ul style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333',
            marginLeft: '30px',
            marginBottom: '20px'
          }}>
            <li style={{ marginBottom: '10px' }}>Email notification to registered users of our <strong>medical billing outsourcing services</strong></li>
            <li style={{ marginBottom: '10px' }}>Prominent notice on our website</li>
            <li style={{ marginBottom: '10px' }}>In-app notifications for users of our <Link to="/healthcare/ai" style={{ color: '#F26522' }}>AI-powered RCM tools</Link></li>
          </ul>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#333' 
          }}>
            We encourage you to review this Privacy Policy regularly to stay informed about how we protect your personal information. Your continued use of our <strong>revenue cycle management consulting services</strong> after policy updates constitutes acceptance of the revised terms.
          </p>
          
          <div style={{
            backgroundColor: '#FFF3E0',
            border: '2px solid #FF9800',
            borderRadius: '8px',
            padding: '20px',
            marginTop: '20px'
          }}>
            <p style={{ 
              fontSize: '15px', 
              fontWeight: '600', 
              color: '#001F2B', 
              marginBottom: '8px' 
            }}>
              📅 Last Updated: June 4, 2025
            </p>
            <p style={{ 
              fontSize: '15px', 
              lineHeight: '1.7', 
              color: '#333' 
            }}>
              This Privacy Policy was last updated on June 4, 2025. Check this page regularly for the most current version governing our <strong>best revenue cycle management companies</strong> practices.
            </p>
          </div>
        </motion.section>

        {/* Section 12 - Contact */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#001F2B',
            color: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            marginBottom: '30px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
          }}
        >
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: '700', 
            marginBottom: '20px',
            color: '#ffffff'
          }}>
            12. Contact Us About Privacy
          </h2>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            marginBottom: '24px',
            color: '#dddddd'
          }}>
            Have questions about our privacy practices or want to learn more about how we protect data in our <strong>healthcare revenue cycle management services</strong>? We're here to help.
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px',
            marginBottom: '30px'
          }}>
            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              padding: '24px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>📧</div>
              <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: '#F26522' }}>
                Email Us
              </h4>
              <a href="mailto:sales@mdercm.com" style={{ 
                color: '#ffffff', 
                fontSize: '16px',
                textDecoration: 'underline'
              }}>
                sales@mdercm.com
              </a>
              <p style={{ fontSize: '14px', color: '#cccccc', marginTop: '8px' }}>
                Privacy inquiries & data requests
              </p>
            </div>

            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              padding: '24px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🇺🇸</div>
              <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: '#F26522' }}>
                USA Office
              </h4>
              <p style={{ fontSize: '15px', color: '#ffffff', lineHeight: '1.6' }}>
                MDeRCM Solutions LLC<br />
                Five Greentree Centre, 525 Route 73 North<br />
                Suite 104, Marlton, NJ 08053, USA<br />
                📞 +1 (510) 356-6069
              </p>
            </div>

            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              padding: '24px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>🇮🇳</div>
              <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: '#F26522' }}>
                India Office
              </h4>
              <p style={{ fontSize: '15px', color: '#ffffff', lineHeight: '1.6' }}>
                MDeRCM Solutions Private Limited<br />
                DLF Forum, Cybercity, Phase III<br />
                Gurugram, Haryana 122002, India<br />
                📞 +91-9311250055
              </p>
            </div>

            <div style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              padding: '24px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.2)'
            }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏰</div>
              <h4 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: '#F26522' }}>
                Response Time
              </h4>
              <p style={{ fontSize: '15px', color: '#ffffff', lineHeight: '1.6' }}>
                We respond to privacy requests within 30 days as required by law.
              </p>
            </div>
          </div>

          <div style={{
            backgroundColor: 'rgba(242, 101, 34, 0.2)',
            border: '2px solid #F26522',
            borderRadius: '8px',
            padding: '24px',
            textAlign: 'center'
          }}>
            <h4 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '12px', color: '#F26522' }}>
              Learn More About Our Services
            </h4>
            <p style={{ fontSize: '15px', lineHeight: '1.7', marginBottom: '20px', color: '#dddddd' }}>
              Interested in our HIPAA-compliant <strong>medical revenue cycle management services</strong>? Explore our solutions or schedule a consultation.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '16px' }}>
              <Link 
                to="/healthcare/ai"
                style={{
                  backgroundColor: '#F26522',
                  color: '#ffffff',
                  padding: '14px 28px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  display: 'inline-block'
                }}
              >
                Explore AI Solutions
              </Link>
              <Link 
                to="/company"
                style={{
                  backgroundColor: 'transparent',
                  color: '#ffffff',
                  padding: '14px 28px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  display: 'inline-block',
                  border: '2px solid #ffffff'
                }}
              >
                Contact Us
              </Link>
            </div>
          </div>
        </motion.section>

        {/* Additional Resources */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeIn}
          style={{
            backgroundColor: '#ffffff',
            padding: '40px',
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
          }}
        >
          <h3 style={{ 
            fontSize: '22px', 
            fontWeight: '700', 
            color: '#001F2B', 
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            Related Resources
          </h3>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '16px'
          }}>
            {[
              { text: 'HIPAA Compliance', link: '/compliance' },
              { text: 'AI Healthcare Solutions', link: '/healthcare/ai' },
              { text: 'Company Information', link: '/company' },
              { text: 'Free Trial', link: '/free-trial' },
              { text: 'Blog & Resources', link: '/blog' }
            ].map((item, i) => (
              <Link 
                key={i}
                to={item.link}
                style={{
                  backgroundColor: '#f9fafb',
                  color: '#001F2B',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  textDecoration: 'none',
                  border: '1px solid #e5e7eb',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#F26522';
                  e.target.style.color = '#ffffff';
                  e.target.style.borderColor = '#F26522';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f9fafb';
                  e.target.style.color = '#001F2B';
                  e.target.style.borderColor = '#e5e7eb';
                }}
              >
                {item.text} →
              </Link>
            ))}
          </div>
        </motion.section>
      </div>
    </div>
    </>
  );
};

export default PrivacyPolicy;